import jwtDecode from 'jwt-decode';
import semver from 'semver';
import { trackPromise } from 'react-promise-tracker';
import { STC_ID, STRATUS_ID } from '../../services/constants';

const MFP_ADDRESS_DEBUG_OVERRIDE = localStorage.getItem('mfpAddressOverride', '')
const REACT_APP_MFP_ADDRESS_OVERRIDE = process.env.REACT_APP_MFP_ADDRESS_OVERRIDE
const MFP_ADDRESS = MFP_ADDRESS_DEBUG_OVERRIDE ? MFP_ADDRESS_DEBUG_OVERRIDE :
    (REACT_APP_MFP_ADDRESS_OVERRIDE ? REACT_APP_MFP_ADDRESS_OVERRIDE : 'http://127.0.0.1:8090');

export const getDomain = (): string =>
    ['stratus.lol', 'scantripcloud.lol', 'dodgestrat.us', 'scantripcloud.xyz', 'scantripcloud.com', 'dispatcherstratus.com']
        .some((d) => window.location.hostname.includes(d))
        ? window.location.hostname.split('.').slice(-2).join('.')
        : 'localhost:3000';

export const getApiDomain = (api: string, region: string = 'us-east-1'): string => {
    const domain = getDomain() ?? 'stratus.lol';
    let apiDomain = domain;
    switch (domain) {
        default:
        case 'stratus.lol':
        case 'scantripcloud.lol':
            apiDomain = 'stratus.lol';
            break;
        case 'dodgestrat.us':
        case 'scantripcloud.xyz':
            apiDomain = 'dodgestrat.us';
            break;
        case 'scantripcloud.com':
        case 'dispatcherstratus.com':
            apiDomain = 'scantripcloud.com';
            break;
    }
    return `${region}.${api}.${apiDomain}`;
}

export const getNtfDomain = (region = 'us-east-1'): string =>
    getApiDomain('ntf', region);

export const getWfxDomain = (region = 'us-east-1'): string =>
    getApiDomain('wfx', region);

export const getSwsDomain = (region = 'us-east-1') =>
    getApiDomain('web', region);

export const getEnvironment = (domain): string => {
    domain = domain || getDomain();

    const ENV: string[] = {
        'localhost:3000': 'development',
        'stratus.lol': 'development',
        'scantripcloud.lol': 'development',
        'scantripcloud.xyz': 'testing',
        'dodgestrat.us': 'testing',
        'dispatcherstratus.com': 'production',
        'scantripcloud.com': 'production',
    };

    return ENV?.[domain] || ENV['stratus.lol'];
}

export const getHost = () => {
    const host = window.location.host;
    const parts = host.split('.');
    let baseHost = parts[parts.length - 2] + '.' + parts[parts.length - 1];
    if (window.location.host === 'localhost:3000') {
        baseHost = 'stratus.lol';
    }
    return baseHost
}

export const isEnvironment = (testEnv: string): bool => {
    const env: string = getEnvironment()?.toLowerCase()
    const ret: bool = (env === testEnv || env.startsWith(testEnv))
    console.debug(`isEnvironment({env} == {testEnv}) == {ret}`)
    return ret;
}

const _sortAndFilter = (input) => {
    const list = input.reduce((filtered, workflow) => {
        if (workflow?.status === "running") {
            if (workflow?.acl?.users?.length > 1 || workflow?.acl?.groups?.length > 0) {
                workflow.shared = true;
            }
            filtered.push(workflow);
        }
        return filtered;
    }, []);
    list.sort((a, b) => a.name.localeCompare(b.name));

    return list;
}

export const getWorkflows = async (token, region, appId = STC_ID) => {
    if (typeof token !== 'string')
        throw new Error('token not a string')
    const { hash } = await getMFPHash(appId);

    const MOCKDATA = [
        {
            "id": crypto.randomUUID(),
            "name": "Workflow 1",
            "acl": {
                "users": [
                    {
                        "name": "Josh",
                        "role": "owner"
                    },
                    {
                        "name": "User1",
                        "role": "view"
                    }
                ]
            },
            "status": "running"
        },
        {
            "id": crypto.randomUUID(),
            "name": "Workflow 2",
            "status": "running"
        },
        {
            "id": crypto.randomUUID(),
            "name": "Workflow 3",
            "acl": {
                "users": [
                    {
                        "name": "Josh",
                        "role": "owner"
                    }
                ],
                "groups": [
                    {
                        "name": "Users",
                        "role": "view"
                    }
                ]
            },
            "status": "running",
        },
        {
            "id": crypto.randomUUID(),
            "name": "Workflow 4",
            "status": "running"
        },
        {
            "id": crypto.randomUUID(),
            "name": "Workflow 5",
            "status": "running"
        },
        {
            "id": crypto.randomUUID(),
            "name": "Workflow 6",
            "acl": {
                "users": [
                    {
                        "name": "Josh",
                        "role": "owner"
                    },
                    {
                        "name": "User1",
                        "role": "view"
                    }
                ],
                "groups": [
                    {
                        "name": "Users",
                        "role": "view"
                    }
                ]
            },
            "status": "running"
        },
        {
            "id": crypto.randomUUID(),
            "name": "Workflow 7",
            "status": "running"
        },
        {
            "id": crypto.randomUUID(),
            "name": "Workflow 8",
            "status": "running"
        },
        {
            "id": crypto.randomUUID(),
            "name": "Workflow 9",
            "status": "running"
        },
        {
            "id": crypto.randomUUID(),
            "name": "This is a workflow with a really long title hahahahahahaha",
            "status": "running"
        },
        {
            "id": crypto.randomUUID(),
            "name": "Scan to Google Drive",
            "status": "running"
        },
        {
            "id": crypto.randomUUID(),
            "name": "DRAFT",
            "status": "draft"
        }
    ]
    const date = Date.now()
    const url = getWfxDomain(region)
    try {
        const resp = await fetch(`https://${url}/api/wfx?status=running&inputs=MfpCapture&date=${date}`, {
            method: 'GET',
            headers: {
                accept: 'application/json',
                Authorization: `Bearer ${token}`,
                'X-Device-Hash': hash
            }
        });
        if (!resp.ok) {
            return { status: resp.status, workflows: [] };
        }
        const json = await resp.json();
        const workflows = _sortAndFilter(json);
        return { status: resp.status, workflows }
    } catch (e) {
        if (window.location.host === 'localhost:3000') {
            await new Promise((resolve) => setTimeout(resolve, 2500));
            return Promise.resolve({ status: 200, workflows: _sortAndFilter(MOCKDATA) });
        }
        console.error('getWorkflows:', e);
        alert(`${e} :: ${date} :: ${region} :: ${url} :: ${process.env.SERVICE_NAME}`)
        return Promise.reject(e);
    }
}

export const getWorkflowConfig = (_id) => {
    const MOCKDATA = 'http://localhost:3000'
    return MOCKDATA
}

export const startWorkflow = async (workflowId, tenant, token, dipa, stratus) => {
    const url = getNtfDomain(tenant.region);
    let appId = STC_ID;
    if (stratus) {
        appId = STRATUS_ID;
    }
    const { hash } = await getMFPHash(appId);
    console.log(hash);
    const response = await fetch(`https://${url}/workflow/start`, {
        method: 'POST',
        body: JSON.stringify({
            workflowId,
            tenant,
            dipa,
            hash,
            metadata: {
                stratus
            }
        }),
        headers: {
            Authorization: `Bearer ${token}`
        }
    });
    if (!response.ok) {
        let json = {}
        try {
            json = await response.json();
        } catch (e) {
            console.error(e);
        }
        return { 'error': response.statusText, 'status': response.status, body: json }
    }
    const json = await response.json();
    return json;
}

export const getLicenseUsage = async (tenant, appId = STC_ID) => {
    const url = getNtfDomain(tenant.region);
    const { hash } = await getMFPHash(appId);
    const response = await fetch(`https://${url}/license/usage/${tenant.id}/MfpCapture/${hash}`);
    if (!response.ok) {
        let json = {}
        try {
            json = await response.json();
        } catch (e) {
            console.error(e);
        }
        return { 'error': response.statusText, 'status': response.status, body: json }
    }
    const json = await response.json();
    return json
}

export const isLicensed = async (tenant, appId = STC_ID) => {
    const response = await getLicenseUsage(tenant, appId);
    if (response.error ||
        response.licenseUsage ||
        response.expired ||
        response.unlicensed) {
        return false;
    }
    return true;
}

export const getWorkflowUI = async (arn, region) => {
    try {
        const url = getNtfDomain(region);
        const response = await fetch(`https://${url}/workflow/ui`, {
            method: 'POST',
            body: JSON.stringify({
                arn
            })
        })
        const json = await response.json();
        return json;
    } catch (e) {
        throw (e);
    }
}

export const reportEnd = async (token, success, url) => {
    const response = await fetch(url, {
        method: 'POST',
        body: JSON.stringify({
            token,
            success
        })
    });
    return response;
}

export const getWfxPreviewImgUrl = (tenantId, workflowId, region) => {
    return `https://${getWfxDomain(region)}/api/wfx/${workflowId}/preview/mfp.png?t=${tenantId}`;
}

export const getMFPHash = async (appId = STC_ID) => {
    const cacheHash = (localStorage.getItem('hash', '') ?? '').replaceAll('"', '');
    const cacheHashVersion = (localStorage.getItem('hashCacheVersion') ?? '').replaceAll('"', '');
    const cacheHashComparer = '2025-10-01';
    if (cacheHash && cacheHashVersion === cacheHashComparer) {
        return {
            hash: cacheHash
        };
    }
    let debug = {}
    try {
        debug.hashRequest = true
        const resp = await sendMFPRequest(`${MFP_ADDRESS}/${appId}/getSerialHash.py`);
        debug.hashResponse = resp.status;
        const json = resp.ok ?
            (await resp.json()) :
            { hash: null, hasScanner: false };
        if (json.hash) {
            // Hash should never change, cache it to increase future load speeds
            localStorage.setItem('hash', json.hash);
            localStorage.setItem('hashCacheVersion', cacheHashComparer);
        }
        if (typeof json.hasScanner === 'boolean') {
            localStorage.setItem('hasScanner', json.hasScanner);
        }
        return {
            hash: json.hash
        }
    } catch (e) {
        console.error(e);
        debug.error = e;
        let s = ''
        for (const key in debug) {
            s += `${key}:${debug[key]},`
        }
        // alert(s);
        console.log(s);
        const debugHash = JSON.parse(localStorage.getItem('debugHash'));
        const debugHashOverride = JSON.parse(localStorage.getItem('debugHashOverride'));
        if (debugHash && (isEnvironment('development') || debugHashOverride)) {
            return {
                hash: debugHash
            };
        }
        return { hash: '' }
    }
}

export const sendLogsToMfp = async (body, appId = STC_ID) => {
    try {
        const resp = await sendMFPRequest(`${MFP_ADDRESS}/${appId}/mfp-log.py`, {
            method: 'post',
            body: JSON.stringify(body)
        });
        const json = await resp.json();
        alert('Logs recorded');
        return json["success"];
    } catch (e) {
        console.error('Unable to send logs to MFP')
        return false
    }
}

export const getMFPSettings = async (appId = STC_ID) => {
    try {
        const resp = await fetch(`${MFP_ADDRESS}/${appId}/getMFPSettings.py`);
        const json = await resp.json();
        return json;
    } catch (e) {
        console.error('getMFPSettings:', e);
        return {}
    }
}

export const getTenants = async (appId = STC_ID) => {
    const MOCKDATA = [
        { "id": "T:11111111-1111-1111-1111-111111111111", "name": "SEC", "slug": "sec", "region": "us-east-1", "plan": "stc" },
        { "id": "T:11111111-1111-1111-1111-111111111112", "name": "Don't Pick This One", "slug": "Blarg", "region": "us-east-1", "plan": "stc" },
        { "id": "T:11111111-1111-1111-1111-111111111113", "name": "Stratus1", "slug": "Stratus", "region": "us-east-1", "plan": "stratus" },
        { "id": "T:11111111-1111-1111-1111-111111111114", "name": "Stratus2", "slug": "Stratus2", "region": "us-east-1", "plan": "stratus" },
        { "id": "T:a67d94ba-69fa-5348-9a2d-220e02019d2c", "name": "james-01-test", "slug": "james-01-test", "region": "us-east-1", "expirationDate": 1726547096000, "plan": "stratus" }
    ]
    // return MOCKDATA
    // return []
    let debug = {};
    try {
        const { hash } = await getMFPHash(appId);
        debug.hash = hash;
        if (hash) {
            const baseHost = getHost();
            debug.firstRequest = true;
            const resp = await fetch(`https://${baseHost}/api/device/tenants/${hash}`);
            debug.firstResponse = resp.status;
            const json = await resp.json();
            return json.map(item => {
                let id = item.id;
                if (id.indexOf("T:") > -1) {
                    id = id.substring(id.indexOf("T:") + 2);
                }
                return { ...item, id }
            });
        }
        return [];
    } catch (e) {
        if (window.location.host === 'mfp.stratus.lol') {
            console.error(e);
            // Used for debugging at the MFP
            let s = ''
            for (const key in debug) {
                s += `${key}:${debug[key]},`
            }
            // alert(s);
            console.log(s);
            return Promise.reject(e);
        }
        if (window.location.host === 'localhost:3000') {
            const MOCKTENANTS = MOCKDATA.map(item => {
                let id = item.id;
                if (id.indexOf("T:") > -1) {
                    id = id.substring(id.indexOf("T:") + 2);
                }
                return { ...item, id }
            });
            return Promise.resolve(MOCKTENANTS);
        }
        console.error(e);
        return Promise.reject(e);
    }
}

export const sendMFPRequest = async (url, options) => {
    let retry = 10;
    const loopMFPRequest = async (url, options) => {
        let response = await _sendMFPRequest(url, options);
        while (response.status === 500 || response.status === 503 || (response.status === 0 && retry > 0)) {
            if (response.status === 0) {
                retry -= 1;
            }
            // Wait a second before trying again
            await new Promise(resolve => setTimeout(resolve, 1000));
            response = await _sendMFPRequest(url, options);
        }
        return response;
    }
    const _sendMFPRequest = async (url, options = {}) => {
        try {
            return await fetch(url, options);
        } catch (e) {
            console.log(e);
            return { ok: false, status: 0 }
        }
    }
    const response = await loopMFPRequest(url, options);
    return response;
}

export const getToken = async (domain, region, code, client_id, client_secret, redirect_uri, grant_type = 'authorization_code') => {
    const formData = {
        code,
        client_id,
        client_secret,
        grant_type,
        redirect_uri
    };
    const params = new URLSearchParams(formData);
    const body = params.toString();
    const tokenResp = await fetch(`https://${domain}.auth.${region}.amazoncognito.com/oauth2/token`, {
        method: 'post',
        body,
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
        }
    });
    return await tokenResp.json();
}

export const revokeToken = async (domain, refresh_token, client_id, client_secret, region = 'us-east-1') => {
    const formData = {
        token: refresh_token,
        client_id
    }
    const params = new URLSearchParams(formData);
    const body = params.toString();

    return await fetch(`https://${domain}.auth.${region}.amazoncognito.com/oauth2/revoke`, {
        method: 'post',
        body,
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'Authorization': `Basic ${btoa(`${client_id}:${client_secret}`)}`
        }
    }).catch(err => {
        console.error('revokeToken:', err);
    });
}

export const refreshToken = async (domain, refresh_token, client_id, client_secret, region = 'us-east-1') => {
    const formData = {
        client_id,
        refresh_token,
        grant_type: 'refresh_token'
    }
    const params = new URLSearchParams(formData);
    const body = params.toString();
    const tokenResp = await fetch(`https://${domain}.auth.${region}.amazoncognito.com/oauth2/token`, {
        method: 'post',
        body,
        headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
            'Authorization': `Basic ${btoa(`${client_id}:${client_secret}`)}`
        }
    });
    let tokenJson = {}
    try {
        tokenJson = await tokenResp.json();
    } catch (e) {
        console.error('Could not parse JSON')
        console.error(e)
    }
    if (tokenResp.status !== 200 || !tokenJson.access_token) {
        throw new Error(`Error refreshing token. Response: ${tokenResp.status} ${JSON.stringify(tokenJson)}`)
    }
    return tokenJson;
}

export const isRelease2me = async (region, tenantId, hash, jwt) => {
    const url = getSwsDomain(region)
    try {
        const response = await fetch(
            `https://${url}/api/device/${tenantId}/hash/${hash}`,
            {
                'Authorization': `Bearer ${jwt}`
            }
        )
        return await response.json()
    } catch (e) {
        return {
            r2mRegistered: false
        }
    }
}

export const getUserPoolClient = async (poolId, clientId, region) => {
    const url = getNtfDomain(region);
    const response = await fetch(`https://${url}/userpool/client`, {
        method: 'post',
        body: JSON.stringify({
            poolId,
            clientId,
            region
        })
    });
    return await response.json();
}
export const getUserPoolId = async (_domain, _region) => {

}

export const getUserPoolClientId = async (_poolId, _region) => {

}

export const getUserPool = async (_poolId, _region) => {

}

export const getUserPoolAll = async (slug, region, tenantId) => {
    const url = getNtfDomain(region);
    const response = await fetch(`https://${url}/userpool/all`, {
        method: 'post',
        body: JSON.stringify({
            slug,
            region,
            tenantId
        })
    });
    return await response.json();
}

export const getUserDataFromToken = async (accessToken, slug, region) => {
    const url = getNtfDomain(region);
    const response = await fetch(`https://${url}/userpool/user`, {
        method: 'post',
        body: JSON.stringify({
            accessToken,
            slug,
            region,
        })
    });
    return await response.json();
}

export const lookupTenantFromIssuer = async (issuer) => {
    const region = issuer.slice(0, issuer.indexOf('_'));
    const url = getWfxDomain(region);
    const response = await fetch(`https://${url}/api/tenants/${issuer}`);
    return await response.json();
}

export const getTokenExpiration = (token) => {
    try {
        const now = Math.floor(Date.now() / 1000);
        const decodedToken = jwtDecode(token);

        if (!decodedToken)
            return -1;

        const ret = decodedToken.exp - now;
        return ret;
    } catch (e) {
        console.error(e);
    }

    return -1;
};

export const hasTokenExpired = (token, zero) => {
    zero = typeof zero !== "undefined" ? zero : 0;

    try {
        const exp = getTokenExpiration(token);
        return exp <= zero;
    } catch (e) {
        console.error(e);
    }
};

export const getMFPUserSettings = async (appId = STC_ID) => {
    try {
        const resp = await fetch(`${MFP_ADDRESS}/${appId}/getMFPSettings.py`);
        if (resp.ok) {
            return await resp.json();
        }
    } catch (e) {
        console.error('getMFPUserSettings:', e);
    }
    return {}
}

export const setMFPUserSettings = async (settings, appId = STC_ID) => {
    try {
        const resp = await fetch(`${MFP_ADDRESS}/${appId}/getMFPSettings.py`, {
            method: 'post',
            body: JSON.stringify(settings)
        });
        if (resp.ok) {
            return await resp.json();
        }
    } catch (e) {
        console.error('setMFPUserSettings:', e);
    }
    return {}
}

export const clearMFPUserSettings = async (appId = STC_ID) => {
    return await setMFPUserSettings({ clear: true }, appId)
}

export const addCookieToMFPList = async (cookie, appId = STC_ID) => {
    try {
        const resp = await fetch(`${MFP_ADDRESS}/${appId}/cookies.py?cookies=${encodeURIComponent(cookie)}`);
        return resp.ok;
    } catch (e) {
        console.error(e);
        return false;
    }
}

export const getCookieDeletionList = async (appId) => {
    try {
        const resp = await fetch(`${MFP_ADDRESS}/${appId}/cookies.json`);
        if (!resp.ok) {
            return [];
        }
        const json = await resp.json();
        if (json?.cookies) {
            return json.cookies;
        }
        return [];
    } catch (e) {
        console.error(e);
        return [];
    }
}

export const restartApp = async (appId) => {
    window.location = `${MFP_ADDRESS}/${appId}`;
    return new Promise(resolve => {
        setTimeout(() => resolve(() => console.log('Waited for app redirect')), 3000)
    })
}

export const isVersionValid = (version) => {
    if (!semver.valid(version)) {
        return false
    }
    return semver.valid(version) && semver.gte(version, '2.6.0');
}

export const getAuthProviders = async (tenant) => {
    try {
        const host = getHost()
        const tenantSlug = tenant.slug
        const tenantId = tenant.id
        const url = `https://${tenantSlug}.tenant.${host}/api/tenants/${tenantId}/auth-providers`
        const resp = await fetch(url)
        const json = await resp.json()
        return json
    } catch (e) {
        console.error(e)
        return []
    }
}

export const getLoginUrl = async (tenant, providerId, cardId) => {
    try {
        const tenantId = tenant.id
        const region = tenant.region
        const host = getSwsDomain(region)
        const url = `https://${host}/api/tenants/${tenantId}/login-url`
        const callback = `${window.location.protocol}//${window.location.host}/auth`;
        let loginStateString = `${tenant?.userPoolId}|${tenant?.userPool?.Domain}|${tenant?.clientId}|${callback}|${tenant.region}|${tenant.id}|${tenant.name}|${tenant.plan}`
        if (cardId) {
            loginStateString += `|${cardId}`
        }
        const loginState = window.btoa(
            unescape(encodeURIComponent(loginStateString))
        );
        const payload = {
            client: 'mfp',
            providerId,
            loginState,
            callback
        }
        const resp = await fetch(url, {
            method: 'POST',
            body: JSON.stringify(payload),
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
        })
        const json = await resp.json()
        return json.loginUrl
    } catch (e) {
        console.error(e)
        throw e
    }
}

export const customLogin = async (username, password, tenant, lastScannedCardId) => {
    try {
        const host = getHost()
        const tenantSlug = tenant.slug
        const url = `https://${tenantSlug}.tenant.${host}/api/tenants/custom-iws-login`
        const payload = {
            username,
            password,
            slug: tenant.slug,
            tenantId: tenant.id,
            cardId: lastScannedCardId,
        }
        const resp = await trackPromise(fetch(url, {
            method: 'POST',
            body: JSON.stringify(payload),
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            }
        }))
        const status = resp.status
        const ok = resp.ok
        const json = await resp.json()
        return { status, ok, body: json }
    } catch (e) {
        console.error(e)
        throw e
    }
}

export const pinLogin = async (pin, tenant, appId) => {
    try {
        const { hash } = await getMFPHash(appId);
        const host = getHost()
        const tenantSlug = tenant.slug
        const url = `https://${tenantSlug}.tenant.${host}/api/tenants/pin-check`
        const payload = {
            pin,
            slug: tenantSlug,
            tenantId: tenant.id,
        }
        const resp = await trackPromise(fetch(url, {
            method: 'POST',
            body: JSON.stringify(payload),
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'X-Device-Hash': hash
            }
        }))
        const status = resp.status
        const ok = resp.ok
        const json = await resp.json()
        return { status, ok, body: json }
    } catch (e) {
        console.error(e)
        throw e
    }
}

export const cardLogin = async (cardNumber, tenant, appId) => {
    try {
        const { hash } = await getMFPHash(appId);
        const host = getHost()
        const tenantSlug = tenant.slug
        const url = `https://${tenantSlug}.tenant.${host}/api/tenants/card-check`
        const payload = {
            cardNumber,
            slug: tenantSlug,
            tenantId: tenant.id,
        }
        const resp = await trackPromise(fetch(url, {
            method: 'POST',
            body: JSON.stringify(payload),
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'X-Device-Hash': hash
            }
        }))
        const status = resp.status
        const ok = resp.ok
        const json = await resp.json()
        return { status, ok, body: json }
    } catch (e) {
        console.error(e)
        throw e
    }
}

export const enableMFPCardListener = async (appId = STC_ID) => {
    const resp = await sendMFPRequest(`${MFP_ADDRESS}/${appId}/startAuthInfo.py`);
    return resp.ok
}

export const getMFPCardInfo = async (appId = STC_ID) => {
    try {
        const resp = await sendMFPRequest(`${MFP_ADDRESS}/${appId}/getAuthInfo.py`);
        if (!resp.ok) {
            // TODO Handling for bad card read
            return { 'error': `Bad card response: ${resp.status}` }
        }
        const json = await resp.json()
        return json
    } catch (e) {
        console.error(e)
        return { 'error': 'Card ID request error' }
    }
}

export const associateCardWithUser = async (badgeNumber, userId, tenantSlug, token) => {
    try {
        const host = getHost()
        const url = `https://${tenantSlug}.tenant.${host}/api/user/badge-number`
        const payload = [{
            badgeNumber,
            userId
        }]
        const resp = await trackPromise(fetch(url, {
            method: 'POST',
            body: JSON.stringify(payload),
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        }))
        const status = resp.status
        const ok = resp.ok
        const json = await resp.json()
        return { status, ok, body: json }
    } catch (e) {
        console.error(e)
        throw e
    }
}
